import { Component, Provide, Ref, Vue } from 'vue-property-decorator'
import { Form, Message, MessageBox } from 'element-ui'
import { namespace } from 'vuex-class'
import { AuthType, TokenType } from '@/model/enums'
import cookies from 'js-cookie'
import $config from '@/config'
import dayjs from 'dayjs'
import { PageNames } from '@/router'
import { ImageCaptcha } from '@/libs/image-captcha'
import { notifySSOAuthLogin, subscribeSSOAuthLogin } from '@/libs/sso-sync'

const appModule = namespace('app')
@Component
export default class LoginController extends Vue {
  // eslint-disable-next-line @typescript-eslint/ban-types
  @appModule.Mutation('setUserInfo') setUserInfo!: Function
  @Ref('refForm') private readonly refForm!: Form
  @Ref('captchaCanvas') private readonly captchaCanvasRef!: HTMLCanvasElement

  private captchaInstance: ImageCaptcha | null = null
  private unsubscribeSSOAuthLogin: (() => void) | null = null

  @Provide() protected formData = {
    userName: '',
    password: '',
    captchaInput: ''
  }

  @Provide() protected rules = {
    userName: [
      { required: true, message: '请输入登录帐号', trigger: 'none' }
    ],
    password: [
      { required: true, message: '请输入登录密码', trigger: 'none' }
    ],
    captchaInput: [
      { required: true, message: '请输入验证码', trigger: 'none' }
    ]
  }

  @Provide() protected loging = false

  protected mounted(): void {
    this.captchaInstance = new ImageCaptcha(this.captchaCanvasRef)
    if (this.redirect) {
      this.unsubscribeSSOAuthLogin = subscribeSSOAuthLogin(this.currentLoginUrl, this.handleSSOAuthSync)
    }
  }

  protected beforeDestroy(): void {
    if (this.unsubscribeSSOAuthLogin) {
      this.unsubscribeSSOAuthLogin()
    }
  }

  private get $from() {
    const { _from = '' } = this.$route.query
    const reg = /^http[s]*:\/\/[dtu]*[-]*portal.visioneschool.com/
    const aiReg = /^http[s]*:\/\/[dtu]*[-]*knowledge.visioneschool.com/
    if (_from && (reg.test(_from as string) || aiReg.test(_from as string))) {
      return _from as string
    }
    return ''
  }

  private get redirect(): string {
    return (this.$route.query?.redirect as string) || ''
  }

  private get currentLoginUrl(): string {
    return `${window.location.origin}${this.$route.path}`
  }

  private notifySSOAuthLoginIfNeeded(authToken: string): void {
    if (!this.redirect) return
    notifySSOAuthLogin(authToken, this.currentLoginUrl)
  }

  private handleLoginSuccessRedirect(authToken: string): void {
    const redirect = this.redirect
    if (redirect) {
      const decodedUrl = decodeURIComponent(redirect)
      const url = new URL(decodedUrl)
      if (this.$route.query && this.$route.query.mode === 'iframe') {
        // 向父窗口发送消息
        window.parent.postMessage({ type: 'authToken', data: authToken }, '*')
        return
      }
      url.searchParams.set('authToken', authToken || '')
      window.location.replace(url.toString())
      return
    }

    // 有 _from的就跳去指定页面
    if (this.$from) {
      window.location.replace(decodeURIComponent(this.$from))
    } else {
      this.$router.replace({ name: PageNames.Home })
    }
  }

  private handleSSOAuthSync = (authToken: string): void => {
    this.handleLoginSuccessRedirect(authToken)
  }

  protected modifyPasswordTip = ''

  protected async login(): Promise<void> {
    try {
      if (this.loging) return
      const ok = await this.refForm.validate()
      if (ok) {
        if (!this.captchaInstance || !this.captchaInstance.verify(this.formData.captchaInput)) {
          Message.error('验证码错误，请重试')
          this.formData.captchaInput = ''
          if (this.captchaInstance !== null) {
            this.captchaInstance.reset()
          }
          return
        }
        this.loging = true
        const ret = await this.$api.LoginAuthentication.LoginController.authentication({
          authType: AuthType.Normal,
          tokenType: TokenType.Normal,
          data: {
            userName: this.formData.userName,
            password: this.formData.password
          }
        })
        this.loging = false
        if (ret.code === 'success' && ret.data) {
          cookies.set($config.keySSOToken, ret.data, {
            expires: dayjs().add(7, 'day').toDate(),
            path: '/',
            domain: '.visioneschool.com'
            // 'sameSite',
            // 'secure'
          })
          this.notifySSOAuthLoginIfNeeded(ret.data)

          if (ret.result && ret.result.oldLogin && ret.result.tips) {
            MessageBox.confirm(ret?.result?.tips || '', '提示', {
              type: 'warning',
              confirmButtonText: '好的',
              showCancelButton: false
            }).then(() => {
              this.$nextTick(() => {
                this.handleLoginSuccessRedirect(ret.data || '')
              })
            })
            return
          }

          this.$nextTick(() => {
            this.handleLoginSuccessRedirect(ret.data || '')
          })
        }
      }
    } catch (error) {
      // Message.error('登录失败: ' + String(error))
    }
  }

  protected handleLinkClick(str: string): void {
    this.modifyPasswordTip = str
  }

  protected handleEnterKey(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !this.loging) {
      this.login()
    }
  }

  protected goBack(): void {
    this.modifyPasswordTip = ''
  }
}
